import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface SuperAdminGuardProps {
  children: React.ReactNode;
}

export const SuperAdminGuard = ({ children }: SuperAdminGuardProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkSuperAdminAccess = async () => {
      try {
        // Check if user is authenticated
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          console.log("[SuperAdminGuard] No session, redirecting to auth");
          navigate("/auth", { replace: true });
          return;
        }

        // Check if user has super_admin role
        const { data: isSuperAdmin, error: roleError } = await supabase
          .rpc("is_super_admin", { _user_id: session.user.id });

        if (roleError) {
          console.error("[SuperAdminGuard] Error checking role:", roleError);
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        if (!isSuperAdmin) {
          console.log("[SuperAdminGuard] User is not super admin");
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        setIsAuthorized(true);
      } catch (error) {
        console.error("[SuperAdminGuard] Error:", error);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSuperAdminAccess();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_OUT" || !session) {
          navigate("/auth", { replace: true });
        } else if (event === "SIGNED_IN") {
          // Recheck super admin status
          checkSuperAdminAccess();
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-4">
          <Alert variant="destructive">
            <AlertDescription className="text-center">
              <strong className="block mb-2">Accès refusé</strong>
              Cette zone est réservée aux super administrateurs uniquement.
            </AlertDescription>
          </Alert>
          <div className="text-center">
            <button
              onClick={() => navigate("/auth")}
              className="text-primary hover:underline"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
